import React, { useState, useEffect } from 'react';
import { Lock, Mail, ArrowRight, Sparkles } from 'lucide-react';
import Logo from '../../components/common/Logo';

import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const hostname = window.location.hostname;
    const mainDomain = 'panel.ugra.io';
    const isDev = import.meta.env.DEV;
    if (!isDev && hostname !== mainDomain && hostname !== `www.${mainDomain}`) {
      window.location.replace(`https://${mainDomain}/login`);
    }
  }, []);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/app');
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred during authentication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-main);
          padding: 2rem;
          background-image: radial-gradient(circle at 20% 20%, rgba(111, 78, 55, 0.05) 0%, transparent 40%),
                            radial-gradient(circle at 80% 80%, rgba(111, 78, 55, 0.05) 0%, transparent 40%);
        }

        .login-card {
          width: 100%;
          max-width: 480px;
          padding: 3.5rem;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 32px;
          box-shadow: var(--shadow-md);
          position: relative;
          overflow: hidden;
        }

        .login-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, transparent, var(--primary), transparent);
        }

        .login-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .login-logo-link {
          display: inline-flex;
          margin-bottom: 1.5rem;
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          text-decoration: none;
        }

        .login-logo-link:hover {
          transform: scale(1.1);
        }

        .login-logo {
          background: var(--primary);
          width: 60px;
          height: 60px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 8px 20px rgba(111, 78, 55, 0.2);
        }

        .welcome-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: rgba(111, 78, 55, 0.08);
          color: var(--primary);
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 700;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .login-title {
          font-size: 2rem;
          font-weight: 800;
          color: var(--text-main);
          margin-bottom: 0.75rem;
          letter-spacing: -0.02em;
        }

        .login-subtitle {
          color: var(--text-muted);
          font-size: 0.9375rem;
          line-height: 1.6;
        }

        .form-group {
          margin-bottom: 1.5rem;
          position: relative;
        }

        .form-label {
          display: block;
          margin-bottom: 0.625rem;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--text-main);
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          top: 42px;
          color: var(--text-muted);
          transition: color 0.2s;
        }

        .login-input {
          width: 100%;
          padding: 0 16px 0 48px;
          height: 54px;
          border-radius: 14px;
          border: 1.5px solid var(--border);
          background: var(--bg-main);
          font-size: 0.9375rem;
          transition: all 0.2s;
          color: var(--text-main);
        }

        .login-input:focus {
          border-color: var(--primary);
          background: white;
          outline: none;
          box-shadow: 0 0 0 4px rgba(111, 78, 55, 0.1);
        }

        .login-input:focus + .input-icon {
          color: var(--primary);
        }

        .login-button {
          width: 100%;
          height: 54px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 1rem;
          font-weight: 700;
          margin-top: 2.5rem;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(111, 78, 55, 0.15);
        }

        .login-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(111, 78, 55, 0.25);
          filter: brightness(1.1);
        }

        .login-button:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-footer {
          margin-top: 2.5rem;
          text-align: center;
          font-size: 0.875rem;
          color: var(--text-muted);
          padding-top: 1.5rem;
          border-top: 1px solid var(--border);
        }

        .error-message {
          background: #fff1f2;
          color: #e11d48;
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          font-size: 0.875rem;
          font-weight: 500;
          text-align: center;
          border: 1px solid #ffe4e6;
          animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
        }

        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        @media (max-width: 520px) {
          .login-container { padding: 1rem; }
          .login-card { padding: 2rem 1.5rem; border-radius: 24px; }
        }
      `}</style>

      <div className="login-card">
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
          <a href="https://ugra.io" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '12px',
            background: 'var(--bg-main)',
            border: '1px solid var(--border)',
            transition: 'all 0.2s'
          }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} /> Ana Sayfa
          </a>
        </div>

        <div className="login-header">
          <Link to="/" className="login-logo-link" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Logo size={64} />
            <span style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', position: 'relative', top: '3px', letterSpacing: '-0.02em', color: 'var(--text-main)' }}>ugra</span>
          </Link>

          <div>
            <div className="welcome-badge">
              <Sparkles size={14} /> {t('dashboard.welcome', 'Tekrar Hoş Geldiniz')}
            </div>
            <h1 className="login-title">İşletme Girişi</h1>
            <p className="login-subtitle">
              Sizin için her şeyi hazırladık. İşletmenizi yönetmeye hemen başlayabilirsiniz.
            </p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleAuth}>
          <div className="form-group">
            <label className="form-label">{t('common.email')}</label>
            <Mail className="input-icon" size={20} />
            <input
              type="email"
              className="login-input"
              placeholder="e-posta@isletme.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('login.password')}</label>
            <Lock className="input-icon" size={20} />
            <input
              type="password"
              className="login-input"
              placeholder="••••••••"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <Link to="/forgot-password" style={{ color: 'var(--primary)', textDecoration: 'none', fontSize: '0.8125rem', fontWeight: 600 }}>Sifremi Unuttum</Link>
          </div>

          <button className="login-button" disabled={loading}>
            {loading ? t('common.loading') : 'Panele Giriş Yap'}
            {!loading && <ArrowRight size={20} />}
          </button>
        </form>

        <div className="login-footer">
          {t('login.noAccount', 'Hesabınız yok mu?')} <Link to="/signup" className="link" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 800, marginLeft: '4px' }}>Hemen Katıl</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
